import type { PerformedAt } from '@util/testCatalogue'
import moment from 'moment'

export type ReferralSlipTest = { key?: string; label: string; performedAt: PerformedAt }

export type ReferralSlipProps = {
  kind: 'lab' | 'imaging'
  facilityName: string
  patient?: {
    first_name?: string | null
    last_name?: string | null
    date_of_birth?: string | null
    gender?: string | null
    ref?: string | null
  } | null
  orderDate?: string | null
  orderedBy?: string | null
  /** Every line of the order; only the `outside` ones are printed. */
  tests: ReferralSlipTest[]
  note?: string | null
  orderNumber?: string
}

export const patientAgeLabel = (dateOfBirth?: string | null, now: moment.Moment = moment()): string => {
  if (!dateOfBirth) return '-'
  const dob = moment(dateOfBirth, moment.ISO_8601)
  if (!dob.isValid()) return '-'
  const years = now.diff(dob, 'years')
  if (years >= 2) return `${years} yrs`
  const months = now.diff(dob, 'months')
  return months >= 1 ? `${months} mo` : `${Math.max(now.diff(dob, 'days'), 0)} d`
}

/**
 * Printable referral for the tests a patient does at an outside lab / imaging centre.
 * Rendered hidden on screen (`print:block`) by the order detail drawers and sent to `window.print()`.
 */
export const ReferralSlip = ({
  kind,
  facilityName,
  patient,
  orderDate,
  orderedBy,
  tests,
  note,
  orderNumber
}: ReferralSlipProps) => {
  const outside = tests.filter(test => test.performedAt === 'outside')
  const title = kind === 'lab' ? 'Laboratory referral' : 'Imaging referral'
  const patientName = `${patient?.first_name ?? ''} ${patient?.last_name ?? ''}`.trim() || '-'

  return (
    <div className="referral-slip bg-white p-8 text-[#111]" data-testid="referral-slip">
      <div className="mb-6 border-b-2 border-black pb-4 text-center">
        <h1 className="text-2xl font-bold uppercase">{facilityName || ' '}</h1>
        <p className="text-sm">{title}</p>
      </div>

      <div className="mb-6 flex items-start justify-between rounded border border-gray-300 p-4 text-sm">
        <div>
          <p className="font-semibold uppercase text-gray-500">Patient</p>
          <p className="text-lg font-bold">{patientName}</p>
          {patient?.ref ? <p>ID: {patient.ref}</p> : null}
          <p>
            Age: {patientAgeLabel(patient?.date_of_birth)} · Sex:{' '}
            <span className="capitalize">{patient?.gender || '-'}</span>
          </p>
        </div>
        <div className="text-right">
          {orderNumber ? <p>Order {orderNumber}</p> : null}
          <p>Date: {orderDate ? moment(orderDate).format('DD/MM/YYYY') : moment().format('DD/MM/YYYY')}</p>
          <p>Requested by: {orderedBy || '-'}</p>
        </div>
      </div>

      <p className="mb-2 text-sm font-semibold uppercase text-gray-500">
        {kind === 'lab' ? 'Tests to be done at an outside laboratory' : 'Studies to be done at an outside centre'}
      </p>
      <table className="w-full border-collapse border border-black">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-black p-2 text-left text-sm uppercase">#</th>
            <th className="border border-black p-2 text-left text-sm uppercase">{kind === 'lab' ? 'Test' : 'Study'}</th>
          </tr>
        </thead>
        <tbody>
          {outside.map((test, i) => (
            <tr key={test.key ?? `${i}-${test.label}`}>
              <td className="border border-black p-2 text-sm">{i + 1}</td>
              <td className="border border-black p-2 text-sm">{test.label}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {note?.trim() ? (
        <div className="mt-4 text-sm">
          <p className="font-semibold uppercase text-gray-500">Note</p>
          <p className="whitespace-pre-wrap">{note}</p>
        </div>
      ) : null}

      <p className="mt-4 text-sm">Please return the results to {facilityName || 'the referring facility'}.</p>

      <div className="mt-12 flex justify-between border-t border-gray-200 pt-8">
        <div className="w-56 text-center">
          <div className="mb-1 border-b border-black"></div>
          <p className="text-xs uppercase">Requesting clinician</p>
        </div>
        <div className="w-56 text-center">
          <div className="mb-1 border-b border-black"></div>
          <p className="text-xs uppercase">Date / stamp</p>
        </div>
      </div>
    </div>
  )
}

export default ReferralSlip
